import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./AllQueriesPopup.css";
import { queryService } from "../../../services/QueryService";
import { X } from "lucide-react";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { toast } from "../../../utils/toast";
import { useLocation, useNavigate } from "react-router-dom";

ModuleRegistry.registerModules([AllCommunityModule]);

interface Query {
  id: string;
  queryId: string;
  user_email: string;
  subject: string;
  category: string;
  status: "Open" | "Resolved";
  last_updated_at: string;
  user_name: string;
  drive_id: string;
  user_id: string;
}

interface AllQueriesPopupProps {
  isOpen?: boolean;
  updateKey?: number;
  onClose?: () => void;
  onViewQuery?: (query: Query) => void;
}

const AllQueriesPopup: React.FC<AllQueriesPopupProps> = ({
  // isOpen,
  // updateKey,
  // onClose,
  // onViewQuery,
}) => {

  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = location.state?.isOpen || false
  const onClose = () => navigate("/dashboard", { state: { showAllQueriesPopup: false } })
  const onViewQuery = (query: Query) => {
    navigate(`/query/${query?.id}`)
  }
  const [queries, setQueries] = useState<Query[]>([]);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    fetchAllQueries();
  }, []);

  // useEffect(() => {
  //   if (updateKey && updateKey > 0) {
  //     fetchAllQueries();
  //   }
  // }, [updateKey]);

  const fetchAllQueries = async () => {
    setLoading(true);
    try {
      const data = await queryService.getData();

      // Capture current page if grid is ready
      const currentPage = gridRef.current?.api.paginationGetCurrentPage();

      // Sort by last_updated_at DESC
      const sorted = [...data].sort((a: any, b: any) => {
        const dateA = a.last_updated_at
          ? new Date(a.last_updated_at.replace(/\[.*\]$/, "")).getTime()
          : 0;
        const dateB = b.last_updated_at
          ? new Date(b.last_updated_at.replace(/\[.*\]$/, "")).getTime()
          : 0;
        return dateB - dateA;
      });
      setQueries(sorted);

      // Restore page after data update
      if (currentPage !== undefined) {
        // Use timeout to ensure AG Grid has processed the row data change
        setTimeout(() => {
          gridRef.current?.api.paginationGoToPage(currentPage);
        }, 0);
      }
    } catch (error) {
      console.error("Error fetching all queries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (query: Query, newStatus: string) => {
    try {
      const updateObj: Record<string, string> = {
        id: query.id,
        subject: query.subject,
        category: query.category,
        status: newStatus,
        user_id: query.user_id,
        drive_id: query.drive_id || "",
      };
      const jsonString = JSON.stringify(updateObj);
      const base64EncodeFun = (str: string) => btoa(unescape(encodeURIComponent(str)));
      const base64Encoded = base64EncodeFun(jsonString);

      await queryService.update(query.id, base64Encoded);
      fetchAllQueries();
    } catch (error) {
      console.error("Error updating query status:", error);
      toast.error("Failed to update status.");
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const cleaned = dateStr.replace(/\[.*\]$/, "");
      const date = new Date(cleaned);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  const exportToCsv = useCallback(() => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: "all_queries_export.csv",
    });
  }, []);

  // Status cell renderer with dropdown
  const StatusCellRenderer = (params: any) => {
    const query = params.data as Query;
    return (
      <select
        className={query.status === "Open" ? "status-open" : "status-resolved"}
        value={query.status}
        onChange={(e) => {
          e.stopPropagation();
          handleStatusChange(query, e.target.value);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="Open" disabled hidden>Open</option>
        <option value="In Progress">In Progress</option>
        <option value="Resolved">Resolved</option>
        <option value="Closed" disabled hidden>Closed</option>
      </select>
    );
  };

  // View button cell renderer
  const ViewCellRenderer = (params: any) => {
    const query = params.data as Query;
    return (
      <button
        className="view-btn"
        onClick={(e) => {
          e.stopPropagation();
          onViewQuery(query);
        }}
      >
        View
      </button>
    );
  };

  // Column definitions matching the ReadUsers.tsx AG Grid pattern
  const columnDefs = useMemo(
    () => [
      {
        field: "queryId",
        headerName: "Query ID",
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 120,
      },
      {
        field: "user_email",
        headerName: "User Email",
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 180,
      },
      {
        field: "subject",
        headerName: "Title",
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 180,
      },
      {
        field: "category",
        headerName: "Category",
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 140,
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 130,
        cellRenderer: StatusCellRenderer,
      },
      {
        field: "last_updated_at",
        headerName: "Last Update",
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 180,
        valueFormatter: (params: any) => formatDate(params.value),
      },
      {
        headerName: "Action",
        sortable: false,
        filter: false,
        resizable: false,
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: ViewCellRenderer,
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      editable: false,
    }),
    [],
  );

  // Keep mounted to preserve AG Grid state
  // if (!isOpen) return null;

  return (
    <div className="allqueries-overlay" style={{ display: isOpen ? "flex" : "none" }}>
      <div className="allqueries-container">
        {/* Header */}
        <div className="allqueries-header">
          <h2>All Queries ({queries.length})</h2>
          <div className="header-actions">
            {/* Export Button */}
            <div>
              <button
                onClick={exportToCsv}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  backgroundColor: "green",
                  borderColor: "green",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                </svg>
                Export
              </button>
            </div>
            <button className="help-popup-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* AG Grid Table */}
        <div style={{ padding: "0 20px 20px" }}>
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              Loading...
            </div>
          ) : queries.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              No queries found.
            </div>
          ) : (
            <div
              className="ag-theme-alpine"
              style={{ height: "65vh", width: "100%" }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={queries}
                getRowId={(params: any) => params.data.id}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={10}
                animateRows={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllQueriesPopup;
